/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { View, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { buttonFloating } from '@/assets/styles/buttonStyles';
import {
  parseDate,
  currency,
  downloadFile,
  hasNoAccess,
} from '@global/helpers';
import * as Layout from '@components/screen/Layout';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import { isWeb, isNative } from '@global/screens';
import ItemDetail from '@components/global/ItemDetail';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import MoreButton from '@components/navbar/MoreButton';
import SelectBox from '@components/form/SelectBox';
import RenderIf from '@components/global/RenderIf';

import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { showPopup } from '@/store/modal';
import { MAX_QUANTITY_FREE_PRODUCTS } from '@/constants';

const dimension = Dimensions.get('window');
const { height: windowHeight } = Dimensions.get('window');

const PromotionRulesDetailScreen = (props) => {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth.user?.role?.code);
  const promotionTypes = useSelector((state) => state.global.promotionTypes);
  const promotionModels = useSelector((state) => state.global.promotionModels);
  const hasEditAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);
  const [detailData, setDetailData] = useState({});
  const [isAfterAction, setIsAfterAction] = useState(false);

  const from = props?.route?.params?.name;

  const showApproval = useMemo(() => {
    return (
      detailData?.current_status == 'need_approval' &&
      Boolean(detailData?.current_approval == userRole)
    );
  }, [detailData, userRole]);
  const hasDuplicateAccess =
    hasEditAccess &&
    ['draft', 'need_approval', 'approved', 'rejected'].includes(
      detailData?.current_status,
    );
  const DuplicatePromo = () => {
    return hasDuplicateAccess ? (
      <Button
        type="primary"
        rounded
        buttonStyle={{ width: 90, marginRight: 10 }}
        title={
          <SociollaText type="bold" style={{ color: Colors.white }}>
            Duplicate
          </SociollaText>
        }
        onPress={() => {
          onDuplicate(detailData._id);
        }}
      />
    ) : (
      false
    );
  };
  const showEditSection =
    hasDuplicateAccess ||
    (hasEditAccess && detailData?.current_status != 'need_approval');

  const getPromotionRulesDetail = useApi(MsOrder.getPromotionRulesDetail);

  const getDetail = async (id) => {
    const response = await getPromotionRulesDetail.request(id);
    const result = response?.data?.data;
    if (result && Object.keys(result).length) {
      setDetailData(result);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setDetailData({});
      if (props?.route?.params?.id) {
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const SectionTitle = ({ title }) => {
    return (
      <View style={{ width: '100%', paddingVertical: isWeb ? 20 : 15 }}>
        <SociollaText
          type="medium"
          style={{ color: Colors.blue0, fontSize: 13 }}>
          {title}
        </SociollaText>
      </View>
    );
  };

  const showLimitPromo = () => {
    return (
      detailData?.no_of_transactions ||
      detailData?.maximum_applied_per_customer ||
      detailData?.maximum_qty_order
    );
  };

  const onExport = async (type = 'products') => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const exportData = await MsOrder.exportDataPromotionRules(
        type,
        {},
        {
          params: {
            filter: {
              _id: props?.route?.params?.id,
            },
          },
        },
      );
      if (exportData?.data?.data) {
        downloadFile(exportData.data.data, `promotion-rules-${type}`, '', true);
        Toast.show({ text1: 'Data has been export.', type: 'success' });
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      Toast.show({ text1: 'A system error occurred', type: 'error' });
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const onChangeStatus = async (status) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const response = await MsOrder.updatePromotionRulesStatus(
        props?.route?.params?.id,
        status,
      );
      if (response?.data?.data) {
        Toast.show({ text1: 'Status has been changed.', type: 'success' });
        getDetail(props?.route?.params?.id);
        setIsAfterAction(true);
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const onDuplicate = async (id) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const duplicatePromotionRules = await MsOrder.duplicatePromotionRules(id);
      if (duplicatePromotionRules?.data?.data?._id) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        props.navigation.replace('PromotionRulesFormScreen', {
          id: duplicatePromotionRules.data.data._id,
          name: detailData?.name ? detailData.name : '-',
          titleScreen: detailData?.name ? detailData.name : '-',
        });
      } else {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'Failed to duplicate promotion', type: 'error' });
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onChangeActive = async (isActive, oldVal) => {
    let newVal = isActive;
    try {
      await MsOrder.updateStatusActivePromotionRules(detailData?._id, {
        is_active: isActive,
      });
    } catch (error) {
      newVal = oldVal.value;
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
    const payload = {
      ...detailData,
      is_active: newVal,
    };
    setDetailData(payload);
  };

  const goToForm = (data) => {
    props.navigation.replace('PromotionRulesFormScreen', {
      id: data._id,
      name: data.name ? data.name : '-',
      titleScreen: data.name ? data.name : '-',
    });
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && !isWeb && showEditSection && detailData?._id) {
      setTimeout(() => {
        const menus = [];
        if (hasEditAccess && detailData?.current_status !== 'need_approval') {
          menus.push({
            type: 'function',
            name: 'Edit',
            callback: () => goToForm(detailData),
          });
        }
        if (hasDuplicateAccess) {
          menus.push({
            type: 'function',
            name: 'Duplicate',
            callback: () => onDuplicate(detailData._id),
          });
        }
        if (menus?.length) {
          props.navigation.setOptions({
            headerRight: () => <MoreButton menu={menus} {...props} />,
          });
        }
      }, 250);
    }
    return () => {
      mounted = false;
    };
  }, [showEditSection, hasEditAccess, hasDuplicateAccess, detailData]);

  const getCustomerTypeLabel = (rules) => {
    if (rules?.market_types?.length) {
      return `Market Type: ${rules.market_types[0].name}`;
    }
    if (rules?.clusters?.length) {
      return 'Specific Cluster';
    }
    if (rules?.customer_types?.length) {
      return 'Specific Customer Type';
    }
    if (rules?.customers?.length) {
      return 'Specific Customer';
    }
    return 'All Customers';
  };

  const LevelProductTable = ({ items, type, isFreeProduct }) => {
    const PRODUCT_COLS = {
      img: 0.6,
      brand: 1.2,
      name: 2.2,
      attr: 1,
      qty: 0.6,
      tax: 0.6,
    };

    const is_brand = type === 'brand';

    if (!items || items.length === 0) {
      return null;
    }

    return (
      <View
        style={{
          borderWidth: 1,
          borderColor: '#D9D9D9',
          borderRadius: 4,
          marginBottom: 30,
          maxHeight: 300,
          overflow: 'scroll',
        }}>
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: '#233876',
            paddingVertical: 10,
            paddingHorizontal: 12,
          }}>
          {is_brand ? (
            <>
              <SociollaText
                type={'regular'}
                style={{ flex: 2, color: 'white', fontSize: 13 }}>
                Brand Name
              </SociollaText>

              <SociollaText
                type={'regular'}
                style={{
                  flex: 1,
                  color: 'white',
                  fontSize: 13,
                  textAlign: 'center',
                }}>
                Qty
              </SociollaText>
            </>
          ) : (
            <>
              <View style={{ flex: PRODUCT_COLS.img }}>
                <SociollaText
                  type={'regular'}
                  style={{ color: 'white', fontSize: 13 }}>
                  Image
                </SociollaText>
              </View>
              <View style={{ flex: PRODUCT_COLS.brand, marginLeft: 2 }}>
                <SociollaText
                  type={'regular'}
                  style={{ color: 'white', fontSize: 13 }}>
                  Brand Name
                </SociollaText>
              </View>
              <View style={{ flex: PRODUCT_COLS.name, marginLeft: 24 }}>
                <SociollaText
                  type={'regular'}
                  style={{ color: 'white', fontSize: 13 }}>
                  Product Name
                </SociollaText>
              </View>
              <View style={{ flex: PRODUCT_COLS.attr, marginLeft: 27 }}>
                <SociollaText
                  type={'regular'}
                  style={{ color: 'white', fontSize: 13 }}>
                  Attribute
                </SociollaText>
              </View>
              <View
                style={{
                  flex: PRODUCT_COLS.qty,
                  alignItems: 'center',
                  marginLeft: 3,
                }}>
                <SociollaText
                  type={'regular'}
                  style={{ color: 'white', fontSize: 13 }}>
                  Qty
                </SociollaText>
              </View>
              <RenderIf condition={isFreeProduct}>
                <View
                  style={{
                    flex: PRODUCT_COLS.tax,
                    alignItems: 'center',
                    marginLeft: 3,
                  }}>
                  <SociollaText
                    type={'regular'}
                    style={{ color: 'white', fontSize: 13 }}>
                    Tax
                  </SociollaText>
                </View>
              </RenderIf>
            </>
          )}
        </View>

        {/* Body */}
        {items.map((it, idx) =>
          is_brand ? (
            <View
              key={it.id || idx}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12,
                backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#FFFFFF',
              }}>
              {/* Brand Name */}
              <View style={{ flex: 2, paddingLeft: 12 }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.name}
                </SociollaText>
              </View>

              {/* Qty */}
              <View style={{ flex: 1, alignItems: 'center' }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.quantity}
                </SociollaText>
              </View>
            </View>
          ) : (
            <View
              key={it.id || idx}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12,
                backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#FFFFFF',
              }}>
              {/* Image */}
              <View style={{ flex: PRODUCT_COLS.img, alignItems: 'center' }}>
                <Image
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                  }}
                  width={48}
                  height={48}
                  source={it.image}
                />
              </View>

              {/* Brand */}
              <View style={{ flex: PRODUCT_COLS.brand, paddingLeft: 12 }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.brand.name || '-'}
                </SociollaText>
              </View>

              {/* Product Name */}
              <View style={{ flex: PRODUCT_COLS.name, paddingLeft: 12 }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.name}
                </SociollaText>
              </View>

              {/* Attribute */}
              <View style={{ flex: PRODUCT_COLS.attr, paddingLeft: 12 }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.variant || 'Non Specify'}
                </SociollaText>
              </View>

              {/* Qty */}
              <View style={{ flex: PRODUCT_COLS.qty, alignItems: 'center' }}>
                <SociollaText
                  type={'regular'}
                  style={{ fontSize: 13, color: '#1A1A1A' }}
                  numberOfLines={1}>
                  {it.quantity}
                </SociollaText>
              </View>

              {/* Qty */}
              <RenderIf condition={isFreeProduct}>
                <View style={{ flex: PRODUCT_COLS.tax, alignItems: 'center' }}>
                  <SociollaText
                    type={'regular'}
                    style={{ fontSize: 13, color: '#1A1A1A' }}
                    numberOfLines={1}>
                    {it.tax_value}%
                  </SociollaText>
                </View>
              </RenderIf>
            </View>
          ),
        )}
      </View>
    );
  };

  const LevelProductMobile = ({ items, type, isFreeProduct }) => {
    return (
      <View style={{ display: 'flex' }}>
        {items.map((it, idx) => (
          <View
            key={it.id || idx}
            style={{
              marginBottom: 16,
              borderBottomWidth: 1,
              borderBottomColor: '#E6E6E6',
              paddingBottom: 12,
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}>
            {type === 'brand' ? (
              <View style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Brand name */}
                <SociollaText
                  type="regular"
                  style={{ fontSize: 12, color: '#1A1A1A', marginBottom: 6 }}
                  numberOfLines={1}>
                  {it.name}
                </SociollaText>

                {/* Qty */}
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <SociollaText
                    style={{ fontSize: 12, color: '#595959', marginRight: 4 }}>
                    Qty:
                  </SociollaText>
                  <SociollaText style={{ fontSize: 12, color: '#1A1A1A' }}>
                    {it.quantity}
                  </SociollaText>
                </View>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', display: 'flex', gap: 12 }}>
                {/* Image */}
                <Image
                  style={{ width: 42, height: 42, borderRadius: 4 }}
                  width={42}
                  height={42}
                  source={it.image}
                />

                <View
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {/* SKU */}
                  {it.sku && (
                    <SociollaText
                      type="regular"
                      style={{ fontSize: 12, color: '#171D29' }}
                      numberOfLines={1}>
                      [{it.sku}]
                    </SociollaText>
                  )}

                  {/* Brand + Product Name */}
                  <View style={{ flexWrap: 'wrap', width: '80%' }}>
                    <SociollaText
                      type="regular"
                      style={{ fontSize: 11, color: '#171D29' }}
                      numberOfLines={2}>
                      {it.brand.name} - {it.name}
                    </SociollaText>
                  </View>

                  {/* Attribute */}
                  <SociollaText style={{ fontSize: 11, color: '#7D838F' }}>
                    {it.variant || 'Non Specify'}
                  </SociollaText>

                  {/* Qty */}
                  <View style={{ flexDirection: 'row', display: 'flex' }}>
                    <SociollaText
                      style={{
                        fontSize: 11,
                        color: '#595959',
                        marginRight: 4,
                      }}>
                      Qty:
                    </SociollaText>
                    <SociollaText style={{ fontSize: 11, color: '#1A1A1A' }}>
                      {it.quantity}
                    </SociollaText>
                  </View>

                  {/* Tax */}
                  <RenderIf condition={isFreeProduct}>
                    <View style={{ flexDirection: 'row', display: 'flex' }}>
                      <SociollaText
                        style={{
                          fontSize: 11,
                          color: '#595959',
                          marginRight: 4,
                        }}>
                        Tax:
                      </SociollaText>
                      <SociollaText style={{ fontSize: 11, color: '#1A1A1A' }}>
                        {it.tax_value}%
                      </SociollaText>
                    </View>
                  </RenderIf>
                </View>
              </View>
            )}
          </View>
        ))}
      </View>
    );
  };

  const renderCustomerRules = (rules) => {
    if (rules?.is_applicable_for_all_customers) {
      return (
        <SociollaText style={styles.itemValueText}>
          - All Customers
        </SociollaText>
      );
    }

    if (rules?.market_types?.length) {
      const marketType = rules.market_types[0];
      const businessTypes = marketType.business_types || [];
      return (
        <>
          {businessTypes.length
            ? businessTypes.map((item, i) => (
                <SociollaText
                  style={styles.itemValueText}
                  key={`market-business-${i}`}>
                  - {item.name}
                </SociollaText>
              ))
            : null}
        </>
      );
    }

    if (rules?.clusters?.length) {
      return (
        <>
          {rules.clusters.map((item, i) => (
            <SociollaText style={styles.itemValueText} key={`cluster-${i}`}>
              - {item.name}
            </SociollaText>
          ))}
        </>
      );
    }

    if (rules?.customer_types?.length) {
      return (
        <>
          {rules.customer_types.map((item, i) => (
            <SociollaText
              style={styles.itemValueText}
              key={`customer-type-${i}`}>
              - {item.name}
            </SociollaText>
          ))}
        </>
      );
    }

    if (rules?.customers?.length) {
      return (
        <>
          {rules.customers.map((item, i) => (
            <SociollaText style={styles.itemValueText} key={`customer-${i}`}>
              - {item.name}
            </SociollaText>
          ))}
        </>
      );
    }

    return (
      <SociollaText style={styles.itemValueText}>
        - No specific customer rule
      </SociollaText>
    );
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <Layout.Back
          title={
            from === 'Promotion Rules Approval' ? 'Task' : 'Promotion Rules'
          }
          onCallback={() => {
            if (from === 'Promotion Rules Approval') {
              props.navigation.navigate('TaskNotificationNavigator', {
                screen: 'TaskNotificationScreen',
                params: {
                  task: true,
                },
              });
            } else if (isAfterAction) {
              props.navigation.push('PromotionRulesScreen', {
                isAfterAction: true,
              });
            } else {
              props.navigation.navigate('PromotionRulesScreen');
            }
          }}
        />
      ) : (
        false
      )}
      <Layout.Wrapper rounded>
        {getPromotionRulesDetail.loading ? (
          <Layout.Loader />
        ) : detailData && Object.keys(detailData).length ? (
          <>
            <Layout.ScrollViewRefresher
              onCallback={() => getDetail(props?.route?.params?.id)}>
              <View
                style={{
                  ...styles.sectionWrapper,
                  ...(isWeb ? {} : { height: windowHeight * 0.92 }),
                }}>
                {isWeb ? (
                  <View
                    style={[
                      styles.rowContainer,
                      {
                        position: 'relative',
                        zIndex: 1,
                        justifyContent: 'space-between',
                      },
                    ]}>
                    <SociollaText type="medium" style={{ fontSize: 20 }}>
                      Promotion Rules
                    </SociollaText>
                    {showApproval ? (
                      <View
                        style={{ position: 'relative', flexDirection: 'row' }}>
                        <DuplicatePromo />
                        <Button
                          type="danger"
                          rounded
                          buttonStyle={{ width: 100, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Reject
                            </SociollaText>
                          }
                          onPress={() => onChangeStatus('rejected')}
                        />
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{ width: 100, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Approved
                            </SociollaText>
                          }
                          onPress={() => onChangeStatus('approved')}
                        />
                      </View>
                    ) : (
                      false
                    )}
                    {isWeb && showEditSection ? (
                      <View
                        style={{ position: 'relative', flexDirection: 'row' }}>
                        <DuplicatePromo />
                        {hasEditAccess &&
                        detailData?.current_status != 'need_approval' ? (
                          <Button
                            type="primary"
                            rounded
                            buttonStyle={{ width: 80, marginRight: 10 }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Edit
                              </SociollaText>
                            }
                            onPress={() => goToForm(detailData)}
                          />
                        ) : (
                          false
                        )}
                      </View>
                    ) : (
                      false
                    )}
                  </View>
                ) : (
                  false
                )}
                <View
                  style={[
                    { maxWidth: isWeb ? 400 : '100%' },
                    isWeb ? { paddingVertical: 15 } : { paddingBottom: 20 },
                  ]}>
                  <ItemDetail
                    name="Name"
                    value={detailData.name ? detailData.name : '-'}
                  />
                  <ItemDetail
                    name="Rules Desc"
                    centered
                    value={
                      detailData.description ? detailData.description : '-'
                    }
                  />
                  <ItemDetail
                    name="Periode"
                    centered
                    value={
                      detailData.from_date || detailData.to_date
                        ? `${
                            detailData.from_date
                              ? parseDate(detailData.from_date)
                              : '-'
                          } to ${
                            detailData.to_date
                              ? parseDate(detailData.to_date)
                              : '-'
                          }`
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Is Active"
                    centered
                    value={
                      !hasNoAccess([
                        'superadmin',
                        'budget_controller',
                        'marketing_admin',
                        'general',
                      ]) ? (
                        <SelectBox
                          label="Status"
                          hideLabel
                          options={[
                            { name: 'Active', value: true },
                            { name: 'Inactive', value: false },
                          ]}
                          value={{
                            name: detailData.is_active ? 'Active' : 'Inactive',
                            value: detailData.is_active,
                          }}
                          trackBy="value"
                          labelKey="name"
                          onChange={(val, oldVal) => {
                            const finalValue = val.value;
                            onChangeActive(finalValue, oldVal);
                          }}
                        />
                      ) : (
                        <StatusBadge
                          status={detailData.is_active ? 'active' : 'in_active'}
                        />
                      )
                    }
                  />
                  <ItemDetail
                    name="Status"
                    centered
                    value={<StatusBadge status={detailData.current_status} />}
                  />
                  <ItemDetail
                    name="Promotion Type"
                    centered
                    value={
                      detailData.promotion_type
                        ? promotionTypes[detailData.promotion_type]
                        : '-'
                    }
                  />
                  <ItemDetail
                    name="Promotion Model"
                    centered
                    value={
                      detailData.promotion_model
                        ? promotionModels[detailData.promotion_model]
                        : '-'
                    }
                  />
                  {detailData.rules || detailData.discount_info ? (
                    <>
                      <SectionTitle
                        title={`${
                          detailData.promotion_type
                            ? promotionTypes[detailData.promotion_type]
                            : '-'
                        } - ${
                          detailData.promotion_model
                            ? promotionModels[detailData.promotion_model]
                            : '-'
                        }`}
                      />
                      {detailData.promotion_model == 'cash_before_delivery' ? (
                        <ItemDetail
                          name="Percent Discount"
                          centered
                          value={
                            detailData?.discount_info?.deduction_percentage +
                              `%` || '-'
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData.discount_info?.cluster?.id ? (
                        <ItemDetail
                          name="Cluster"
                          centered
                          value={
                            detailData?.discount_info?.cluster?.name || '-'
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData.discount_info?.cluster?.id ? (
                        <ItemDetail
                          name="Percent Discount"
                          centered
                          value={
                            detailData?.discount_info?.deduction_percentage +
                              '%' || '-'
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData.rules?.sales_team?.length ||
                      detailData.rules?.is_applicable_for_all_sales_team ? (
                        <ItemDetail
                          name="Sales Team"
                          valueComponent={
                            <View style={styles.detailItem}>
                              <SociollaText style={styles.rulesTitle}>
                                Specific Sales Team
                              </SociollaText>
                              {detailData.rules
                                ?.is_applicable_for_all_sales_team ? (
                                <SociollaText style={styles.itemValueText}>
                                  - All Sales Team
                                </SociollaText>
                              ) : (
                                <SociollaText style={styles.itemValueText}>
                                  -{' '}
                                  {detailData.rules?.sales_team?.length
                                    ? detailData.rules.sales_team
                                        .map((a) => a.name)
                                        .join(', ')
                                    : ''}
                                </SociollaText>
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData.rules?.customers?.length ||
                      detailData.rules?.market_types?.length ||
                      detailData.rules?.clusters?.length ||
                      detailData.rules?.is_applicable_for_all_customers ||
                      detailData.rules?.customer_types?.length > 0 ? (
                        <ItemDetail
                          name={
                            ![
                              'extra_discount',
                              'buy_x_get_x_and_buy_x_get_y',
                            ].includes(detailData?.promotion_model)
                              ? 'Customer'
                              : ''
                          }
                          nameComponent={
                            [
                              'extra_discount',
                              'buy_x_get_x_and_buy_x_get_y',
                            ].includes(detailData?.promotion_model) ? (
                              <View style={{ flexDirection: 'row' }}>
                                <SociollaText
                                  style={{ fontSize: 12, color: Colors.grey1 }}>
                                  Customer
                                </SociollaText>
                                {detailData.rules?.customers?.length ||
                                  detailData.rules
                                    ?.is_applicable_for_all_customers ||
                                  (detailData.rules?.customer_types?.length >
                                    0 && (
                                    <TouchableOpacity
                                      activeOpacity={0.8}
                                      style={styles.exportIcon}
                                      onPress={() => onExport('customers')}>
                                      <FontAwesome
                                        name="download"
                                        size={13}
                                        color={Colors.white}
                                      />
                                    </TouchableOpacity>
                                  ))}
                              </View>
                            ) : (
                              ''
                            )
                          }
                          valueComponent={
                            <View style={styles.detailItem}>
                              <SociollaText style={styles.rulesTitle}>
                                {getCustomerTypeLabel(detailData.rules)}
                              </SociollaText>
                              {renderCustomerRules(detailData.rules)}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                      {(detailData?.promotion_model ==
                        'buy_x_get_x_and_buy_x_get_y' &&
                        detailData.rules.brands?.length) ||
                      detailData.rules?.is_applicable_for_all_product ===
                        false ? (
                        <ItemDetail
                          name="Purchase Brands"
                          valueComponent={
                            <View style={styles.detailItem}>
                              {detailData.rules
                                ?.is_applicable_for_all_products ? (
                                <>
                                  {detailData.rules.brands?.length ? (
                                    <>
                                      {detailData.rules.brands.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`brand-${i}`}>
                                            - {item.name}
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              ) : (
                                false
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                      {(detailData?.promotion_model ==
                        'buy_x_get_x_and_buy_x_get_y' &&
                        detailData.rules.brands?.length) ||
                      detailData.rules?.is_applicable_for_all_product ===
                        false ? (
                        <ItemDetail
                          name="Minimum Purchased"
                          valueComponent={
                            <View style={styles.detailItem}>
                              {detailData.rules
                                ?.is_applicable_for_all_products ? (
                                <>
                                  {detailData.rules.brands?.length ? (
                                    <>
                                      {detailData.rules.brands.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`brand-${i}`}>
                                            -{' '}
                                            {currency(
                                              item.minimum_purchase,
                                              true,
                                            )}
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              ) : (
                                false
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData.rules?.categories?.length ||
                      detailData.rules?.is_applicable_for_all_product ? (
                        <ItemDetail
                          name="Category"
                          valueComponent={
                            <View style={styles.detailItem}>
                              <SociollaText style={styles.rulesTitle}>
                                Specific Category
                              </SociollaText>
                              {detailData.rules
                                ?.is_applicable_for_all_products ? (
                                <SociollaText style={styles.itemValueText}>
                                  - All Products
                                </SociollaText>
                              ) : (
                                <>
                                  {detailData.rules.categories?.length ? (
                                    <>
                                      {detailData.rules.categories.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`category-${i}`}>
                                            - {item.name}
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}

                      {detailData.promotion_model == 'extra_discount' ||
                      detailData.rules?.is_applicable_for_all_product ? (
                        <ItemDetail
                          name="Brand"
                          valueComponent={
                            <View style={styles.detailItem}>
                              <SociollaText style={styles.rulesTitle}>
                                Specific Brand
                              </SociollaText>
                              {detailData.rules
                                ?.is_applicable_for_all_products ? (
                                <SociollaText style={styles.itemValueText}>
                                  - All Products
                                </SociollaText>
                              ) : (
                                <>
                                  {detailData.rules.brands?.length ? (
                                    <>
                                      {detailData.rules.brands.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`brands-${i}`}>
                                            - {item.name}
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}

                      {/* Purchase Product */}
                      {detailData.promotion_model ==
                      'buy_x_get_x_and_buy_x_get_y' ? (
                        <>
                          {detailData.rules?.products?.length ? (
                            <>
                              <ItemDetail
                                name="Purchased Product"
                                valueComponent={
                                  <View
                                    style={[
                                      styles.detailItem,
                                      {
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                      },
                                    ]}>
                                    <Image
                                      style={{
                                        width: 36,
                                        height: 36,
                                        marginRight: 10,
                                      }}
                                      width={36}
                                      height={36}
                                      source={{
                                        uri: detailData.rules.products[0].image,
                                      }}
                                    />
                                    <View
                                      style={{ width: 'calc(100% - 45px)' }}>
                                      <SociollaText
                                        type="medium"
                                        style={styles.itemValueText}>
                                        {detailData.rules.products[0].brand &&
                                        detailData.rules.products[0].brand.name
                                          ? `${detailData.rules.products[0].brand.name} - `
                                          : ''}{' '}
                                        {detailData.rules.products[0].name}
                                      </SociollaText>
                                      <SociollaText
                                        style={{
                                          fontSize: 12,
                                          color: Colors.grey1,
                                        }}>
                                        [{detailData.rules.products[0].sku}]
                                      </SociollaText>
                                    </View>
                                  </View>
                                }
                              />
                              <ItemDetail
                                name="Quantity Purchased"
                                value={
                                  detailData.rules.products[0].quantity
                                    ? detailData.rules.products[0].quantity
                                    : '0'
                                }
                              />
                            </>
                          ) : (
                            false
                          )}
                          {detailData.discount_info?.free_products?.id ? (
                            <>
                              <ItemDetail
                                name="Free Product"
                                valueComponent={
                                  <View
                                    style={[
                                      styles.detailItem,
                                      {
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                      },
                                    ]}>
                                    <Image
                                      style={{
                                        width: 36,
                                        height: 36,
                                        marginRight: 10,
                                      }}
                                      width={36}
                                      height={36}
                                      source={{
                                        uri: detailData.discount_info
                                          .free_products.image,
                                      }}
                                    />
                                    <View
                                      style={{ width: 'calc(100% - 45px)' }}>
                                      <SociollaText
                                        type="medium"
                                        style={styles.itemValueText}>
                                        {detailData.discount_info.free_products
                                          .brand &&
                                        detailData.discount_info.free_products
                                          .brand.name
                                          ? `${detailData.discount_info.free_products.brand.name} - `
                                          : ''}{' '}
                                        {
                                          detailData.discount_info.free_products
                                            .name
                                        }
                                      </SociollaText>
                                      <SociollaText
                                        style={{
                                          fontSize: 12,
                                          color: Colors.grey1,
                                        }}>
                                        [
                                        {
                                          detailData.discount_info.free_products
                                            .sku
                                        }
                                        ]
                                      </SociollaText>
                                    </View>
                                  </View>
                                }
                              />
                              <ItemDetail
                                name="Quantity Free Product"
                                value={
                                  detailData.discount_info.free_products
                                    .quantity
                                    ? detailData.discount_info.free_products
                                        .quantity
                                    : '0'
                                }
                              />
                              <ItemDetail
                                name="Tax Free Product"
                                value={
                                  detailData.discount_info.free_products
                                    .tax_value
                                    ? `${detailData.discount_info.free_products.tax_value}%`
                                    : '0%'
                                }
                              />
                            </>
                          ) : (
                            false
                          )}
                          {detailData?.rules?.is_gwp_levelling ? (
                            <>
                              <ItemDetail
                                name="Minimum Requirements"
                                value={
                                  detailData?.rules?.gwp_levelling_type ===
                                  'amount'
                                    ? `Amount, ${currency(
                                        detailData?.rules
                                          ?.gwp_levelling_min_amount || 0,
                                        true,
                                      )}`
                                    : `Quantity Purchased, ${
                                        detailData?.rules
                                          ?.gwp_levelling_min_quantity || 0
                                      }`
                                }
                              />
                              <ItemDetail
                                name="Purchase Product By"
                                value={
                                  detailData?.rules
                                    ?.gwp_levelling_purchase_type === 'brand'
                                    ? 'Brand'
                                    : 'Product'
                                }
                              />
                            </>
                          ) : null}
                          <RenderIf
                            condition={detailData?.rules?.is_gwp_levelling}>
                            <>
                              {isWeb && (
                                <View
                                  style={{
                                    width: 700,
                                    marginTop: 25,
                                  }}>
                                  <SociollaText
                                    type={'regular'}
                                    style={{
                                      fontSize: 12,
                                      color: Colors.grey1,
                                      marginBottom: 20,
                                    }}>
                                    Purchase Product
                                  </SociollaText>

                                  {/* purchase product - product */}
                                  {detailData?.rules?.gwp_levelling_products?.map(
                                    (lvl) => (
                                      <View key={`purchase-${lvl.level}`}>
                                        <SociollaText
                                          type={'regular'}
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductTable
                                          items={lvl.products}
                                          type={'product'}
                                        />
                                      </View>
                                    ),
                                  )}

                                  {/* purchase product - brand */}
                                  {detailData?.rules?.gwp_levelling_brands?.map(
                                    (lvl) => (
                                      <View key={`purchase-${lvl.level}`}>
                                        <SociollaText
                                          type={'regular'}
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductTable
                                          items={lvl.brands}
                                          type={'brand'}
                                        />
                                      </View>
                                    ),
                                  )}

                                  {/* Free product - product */}
                                  <SociollaText
                                    type={'regular'}
                                    style={{
                                      fontSize: 12,
                                      color: Colors.grey1,
                                      marginBottom: 20,
                                    }}>
                                    Free Product
                                  </SociollaText>
                                  {detailData?.discount_info?.gwp_levelling_free_products?.map(
                                    (lvl) => (
                                      <View key={`purchase-${lvl.level}`}>
                                        <SociollaText
                                          type={'regular'}
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductTable
                                          items={lvl.products}
                                          type={'product'}
                                          isFreeProduct={true}
                                        />
                                      </View>
                                    ),
                                  )}
                                </View>
                              )}
                              {!isWeb && (
                                <View style={{ marginTop: 20 }}>
                                  {/* Purchase Product */}
                                  <SociollaText
                                    type="regular"
                                    style={{
                                      fontSize: 12,
                                      color: Colors.grey1,
                                      marginBottom: 12,
                                    }}>
                                    Purchase Product
                                  </SociollaText>

                                  {/* product */}
                                  {detailData?.rules?.gwp_levelling_products?.map(
                                    (lvl) => (
                                      <View
                                        key={`purchase-${lvl.level}`}
                                        style={{ marginBottom: 20 }}>
                                        <SociollaText
                                          type="regular"
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductMobile
                                          items={lvl.products}
                                          type={'product'}
                                        />
                                      </View>
                                    ),
                                  )}

                                  {/* brand */}
                                  {detailData?.rules?.gwp_levelling_brands?.map(
                                    (lvl) => (
                                      <View
                                        key={`purchase-${lvl.level}`}
                                        style={{ marginBottom: 20 }}>
                                        <SociollaText
                                          type="regular"
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductMobile
                                          items={lvl.brands}
                                          type={'brand'}
                                        />
                                      </View>
                                    ),
                                  )}

                                  {/* Free Product */}
                                  <SociollaText
                                    type="regular"
                                    style={{
                                      fontSize: 12,
                                      color: Colors.grey1,
                                      marginBottom: 12,
                                    }}>
                                    Free Product
                                  </SociollaText>

                                  {detailData?.discount_info?.gwp_levelling_free_products?.map(
                                    (lvl) => (
                                      <View
                                        key={`free-${lvl.level}`}
                                        style={{ marginBottom: 20 }}>
                                        <SociollaText
                                          type="regular"
                                          style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                          }}>
                                          Level {lvl.level}
                                        </SociollaText>

                                        <LevelProductMobile
                                          items={lvl.products}
                                          type={'product'}
                                          isFreeProduct={true}
                                        />
                                      </View>
                                    ),
                                  )}
                                </View>
                              )}
                            </>
                          </RenderIf>
                        </>
                      ) : detailData.rules?.products?.length ||
                        detailData.rules?.is_applicable_for_all_products ? (
                        <ItemDetail
                          name={
                            detailData?.promotion_model != 'extra_discount'
                              ? 'Product'
                              : ''
                          }
                          nameComponent={
                            detailData?.promotion_model == 'extra_discount' ? (
                              <View style={{ flexDirection: 'row' }}>
                                <SociollaText
                                  style={{ fontSize: 12, color: Colors.grey1 }}>
                                  Product
                                </SociollaText>
                                <TouchableOpacity
                                  activeOpacity={0.8}
                                  style={styles.exportIcon}
                                  onPress={() => onExport()}>
                                  <FontAwesome
                                    name="download"
                                    size={13}
                                    color={Colors.white}
                                  />
                                </TouchableOpacity>
                              </View>
                            ) : (
                              ''
                            )
                          }
                          valueComponent={
                            <View style={styles.detailItem}>
                              <SociollaText style={styles.rulesTitle}>
                                Specific Product
                              </SociollaText>
                              {detailData.rules
                                ?.is_applicable_for_all_products ? (
                                <SociollaText style={styles.itemValueText}>
                                  - All Products
                                </SociollaText>
                              ) : (
                                <>
                                  {detailData.rules.products?.length ? (
                                    <>
                                      {detailData.rules.products.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`product-${i}`}>
                                            - [{item.sku}] {item.name}
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                      {detailData?.promotion_model == 'extra_discount' &&
                      detailData?.discount_info?.discount_type ? (
                        <ItemDetail
                          nameComponent={
                            detailData?.promotion_model == 'extra_discount' ? (
                              <View style={{ flexDirection: 'row' }}>
                                <SociollaText
                                  style={{ fontSize: 12, color: Colors.grey1 }}>
                                  Percent Discount
                                </SociollaText>
                              </View>
                            ) : (
                              ''
                            )
                          }
                          valueComponent={
                            <View style={styles.detailItem}>
                              {detailData?.discount_info?.percentage_type ==
                                'all' &&
                              detailData?.discount_info?.deduction_percentage !=
                                0 ? (
                                <>
                                  <SociollaText style={styles.itemValueText}>
                                    -{' '}
                                    {
                                      detailData?.discount_info
                                        ?.deduction_percentage
                                    }{' '}
                                    %
                                  </SociollaText>
                                </>
                              ) : (
                                false
                              )}
                              {detailData?.discount_info?.percentage_type ==
                              'specific_order' ? (
                                <View>
                                  <SociollaText style={styles.itemValueText}>
                                    - First Order{' '}
                                    {
                                      detailData?.discount_info?.specific_order
                                        ?.first_order_deduction_percentage
                                    }{' '}
                                    %
                                  </SociollaText>
                                  <SociollaText style={styles.itemValueText}>
                                    - Next Order{' '}
                                    {
                                      detailData?.discount_info?.specific_order
                                        ?.next_order_deduction_percentage
                                    }{' '}
                                    %
                                  </SociollaText>
                                </View>
                              ) : (
                                <>
                                  {detailData?.discount_info
                                    ?.specific_quantity_order?.length ? (
                                    <>
                                      {detailData.discount_info.specific_quantity_order.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`category-${i}`}>
                                            - {item.from_qty} - {item.to_qty}{' '}
                                            discount {item.deduction_percentage}{' '}
                                            %
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                  {detailData?.discount_info
                                    ?.specific_amount_order?.length ? (
                                    <>
                                      {detailData.discount_info.specific_amount_order.map(
                                        (item, i) => (
                                          <SociollaText
                                            style={styles.itemValueText}
                                            key={`amount-${i}`}>
                                            - {currency(item.from_amount, true)}{' '}
                                            - {currency(item.to_amount, true)}{' '}
                                            discount {item.deduction_percentage}
                                            %
                                          </SociollaText>
                                        ),
                                      )}
                                    </>
                                  ) : (
                                    false
                                  )}
                                </>
                              )}
                            </View>
                          }
                        />
                      ) : (
                        false
                      )}
                    </>
                  ) : (
                    false
                  )}

                  {showLimitPromo() ? (
                    <>
                      <SectionTitle title="Limit Promo" />
                      <ItemDetail
                        name="No. of Transactions"
                        value={
                          detailData.no_of_transactions
                            ? detailData.no_of_transactions
                            : '-'
                        }
                      />
                      <ItemDetail
                        name="Max applied / customer"
                        value={
                          detailData.maximum_applied_per_customer
                            ? detailData.maximum_applied_per_customer
                            : '-'
                        }
                      />
                      {/* mock data for optional gwp leveling max quantity free product option */}
                      {detailData.promotion_model ==
                      'buy_x_get_x_and_buy_x_get_y' ? (
                        <ItemDetail
                          name="Max quantity free product"
                          value={
                            detailData.maximum_qty_order
                              ? `${currency(detailData.maximum_qty_order)}${
                                  detailData?.rules?.maximum_qty_order_type
                                    ? ' - ' +
                                      (MAX_QUANTITY_FREE_PRODUCTS.find(
                                        (val) =>
                                          val.value ===
                                          detailData.rules
                                            .maximum_qty_order_type,
                                      )?.name || '')
                                    : ''
                                }`
                              : '-'
                          }
                        />
                      ) : (
                        false
                      )}
                    </>
                  ) : (
                    false
                  )}
                  {['extra_discount'].includes(detailData?.promotion_model) ? (
                    <ItemDetail
                      name="Budget Pocket"
                      centered
                      value={
                        detailData?.budget?.name ? detailData.budget.name : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                </View>
              </View>
            </Layout.ScrollViewRefresher>
            {!isWeb ? (
              <>
                {showApproval ? (
                  <View style={buttonFloating}>
                    <View style={{ flexDirection: 'row' }}>
                      <View style={{ width: '50%', paddingRight: 5 }}>
                        <Button
                          type="danger"
                          rounded
                          style={{ width: '100%' }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 16,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Reject
                            </SociollaText>
                          }
                          onPress={() => onChangeStatus('rejected')}
                        />
                      </View>
                      <View style={{ width: '50%', paddingLeft: 5 }}>
                        <Button
                          type="primary"
                          rounded
                          style={{ width: '100%' }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 16,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Approve
                            </SociollaText>
                          }
                          onPress={() => onChangeStatus('approved')}
                        />
                      </View>
                    </View>
                  </View>
                ) : (
                  false
                )}
              </>
            ) : (
              false
            )}
          </>
        ) : (
          <View style={{ width: '100%', padding: 20 }}>
            <SociollaText>No data found.</SociollaText>
          </View>
        )}
      </Layout.Wrapper>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { width: '100%', padding: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
  detailItem: {
    width: !isNative ? 'calc(100% - 130px)' : dimension.width - 160,
  },
  rulesTitle: { fontSize: 14, marginBottom: 7 },
  itemValueText: { fontSize: 13, marginBottom: 3 },
  exportIcon: {
    marginLeft: 5,
    backgroundColor: Colors.blue4,
    borderRadius: 3,
    paddingHorizontal: 5,
    paddingVertical: 4,
  },
});

export default PromotionRulesDetailScreen;
